import { inject, injectable } from 'inversify';
import type { CommandHistory } from './CommandHistory';
import type { ICommand, ICommandProcessor } from '../interfaces/Command';
import { TYPES as CORE_TYPES } from '../ioc/types';
import { AnonymousCommand } from '../commands/Anonymous.command';

@injectable()
export class CommandProcessor implements ICommandProcessor {
  private _commandHistory: CommandHistory;

  private _isCommandBeingExecuted = false;

  public constructor(@inject(CORE_TYPES.CommandHistory) commandHistory: CommandHistory) {
    this._commandHistory = commandHistory;
  }

  public async execute(command: ICommand): Promise<void> {
    if (this._isCommandBeingExecuted) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
      setTimeout(() => this.execute(command), 0);
      return;
    }

    let executionResult: undefined | boolean;
    try {
      this._isCommandBeingExecuted = true;
      executionResult = await command.execute();
    } finally {
      this._isCommandBeingExecuted = false;
    }
    if (command.undo && (executionResult === true || executionResult === undefined)) {
      this._commandHistory.append(command);
    }
  }

  public async executeAnonymousCommand(
    execute: () => Promise<undefined | boolean>,
    undo?: () => Promise<undefined | boolean>
  ): Promise<void> {
    const command = new AnonymousCommand(execute, undo);
    return this.execute(command);
  }

  public addAnonymousCommand(
    execute: () => Promise<undefined | boolean>,
    undo: () => Promise<undefined | boolean>
  ): void {
    const command = new AnonymousCommand(execute, undo);
    this._commandHistory.append(command);
  }

  public async undo(): Promise<void> {
    const command = this._commandHistory.getForUndo();
    if (command?.undo) {
      await command.undo();
    }
  }

  public async redo(): Promise<void> {
    const command = this._commandHistory.getForRedo();
    if (command) {
      await command.execute();
    }
  }

  public startCommandCompound(): void {
    if (this._isCommandBeingExecuted) {
      setTimeout(() => this.startCommandCompound(), 0);
      return;
    }
    this._commandHistory.startCommandCompound();
  }

  public finishCommandCompound(): void {
    if (this._isCommandBeingExecuted) {
      setTimeout(() => this.finishCommandCompound(), 0);
      return;
    }
    this._commandHistory.finishCommandCompound();
  }
}
